/**
 * MetricPills - EfficiencyBadge and MetricPill components
 * for displaying simulation metrics.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { vortxTypography } from '@vortx/shared-ui';
import { COLORS, fadeInUp } from './ResultsDesignTokens';

// Compact efficiency display for header
export const EfficiencyBadge = ({ value }) => (
    <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 1.5,
        px: 2.5,
        bgcolor: alpha(COLORS.efficiency, 0.1),
        border: `1px solid ${alpha(COLORS.efficiency, 0.3)}`,
        borderRadius: 2,
        animation: `${fadeInUp} 0.5s ease-out`,
    }}>
        <Box sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            border: `3px solid ${COLORS.efficiency}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 0 20px ${alpha(COLORS.efficiency, 0.4)}, inset 0 0 15px ${alpha(COLORS.efficiency, 0.1)}`,
        }}>
            <Typography sx={{
                fontFamily: vortxTypography.heading,
                fontSize: '1rem',
                fontWeight: 800,
                color: COLORS.efficiency,
                textShadow: `0 0 10px ${COLORS.efficiency}`,
            }}>
                {value != null ? value.toFixed(0) : '--'}%
            </Typography>
        </Box>
        <Box>
            <Typography sx={{
                fontFamily: vortxTypography.heading,
                fontSize: '1.5rem',
                fontWeight: 700,
                color: COLORS.efficiency,
                lineHeight: 1,
                textShadow: `0 0 20px ${alpha(COLORS.efficiency, 0.5)}`,
            }}>
                {value != null ? value.toFixed(1) : '--'}%
            </Typography>
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.65rem',
                color: COLORS.text.muted,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
            }}>
                System Efficiency
            </Typography>
        </Box>
    </Box>
);

// Metric pill for the row below chart
export const MetricPill = ({ icon: Icon, value, label, unit, color, delay = 0 }) => (
    <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        bgcolor: COLORS.bg.card,
        borderRadius: 2,
        border: `1px solid ${alpha(color, 0.2)}`,
        animation: `${fadeInUp} 0.5s ease-out ${delay}s both`,
        transition: 'all 0.3s ease',
        flex: 1,
        minWidth: 0,
        '&:hover': {
            border: `1px solid ${alpha(color, 0.5)}`,
            boxShadow: `0 0 20px ${alpha(color, 0.15)}`,
        },
    }}>
        <Box sx={{
            p: 1,
            borderRadius: '50%',
            bgcolor: alpha(color, 0.15),
            display: 'flex',
        }}>
            <Icon sx={{ color, fontSize: 20 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{
                fontFamily: vortxTypography.heading,
                fontSize: '1.25rem',
                fontWeight: 700,
                color: COLORS.text.primary,
                lineHeight: 1.1,
            }}>
                {value}
                <Typography component="span" sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.65rem',
                    color: COLORS.text.secondary,
                    ml: 0.5,
                }}>
                    {unit}
                </Typography>
            </Typography>
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.6rem',
                color: COLORS.text.muted,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }}>
                {label}
            </Typography>
        </Box>
    </Box>
);
